// src/lib/utils/format/input.ts
//
// Shared helpers for *interactive number inputs* (calculators, the NumberInput
// control). These are deliberately a separate concern from the display
// formatters in `./numbers`:
//
//   - `./numbers` formats committed, finite values and THROWS on non-finite
//     input (it's a presentation contract for already-validated data).
//   - this module masks/parses raw, in-progress *user keystrokes* — strings
//     that are routinely partial ("1,", "12.", "") — so it never throws and
//     uses fraction-digit *ranges* the fixed-`decimals` `./numbers` API can't
//     express.
//
// Consolidated from three drifted copies (A10b / A2 "absorb the strays"):
// the identical `formatNumberInput`/`parseFormattedNumber` in
// `CPMCalculator`/`CPVCalculator`, and the `formatNumberForDisplay`/
// `parseFormattedNumber` shipped inside `components/shared/NumberInput`.

/**
 * Mask a raw input string as the user types: keep only digits (plus a single
 * decimal point for decimal fields), group the integer part with commas, and
 * cap the fractional part at two places. Never throws — partial input like
 * `"1,"` or `"12."` is expected.
 *
 * @param {string} value - The raw input value (as typed)
 * @param {boolean} [isDecimal=false] - Whether to permit a decimal point
 * @returns {string} The masked input string (e.g. "1,234" or "1,234.56")
 *
 * @example
 * formatNumberInput("1234")        // "1,234"
 * formatNumberInput("1234.567", true) // "1,234.56"
 * formatNumberInput("12.", true)   // "12."
 * formatNumberInput("")            // ""
 */
export function formatNumberInput(value: string, isDecimal = false): string {
  // Remove all non-numeric characters except decimal point for decimal fields
  const cleanValue = isDecimal ? value.replace(/[^\d.]/g, '') : value.replace(/\D/g, '')

  // Handle decimal numbers
  if (isDecimal) {
    const parts = cleanValue.split('.')
    if (parts.length > 2) return parts[0] + '.' + parts[1] // Only allow one decimal point

    // Format the whole number part with commas
    const wholeNumber = parts[0] ? parseInt(parts[0]).toLocaleString('en-US') : '0'

    // Add decimal part if it exists
    if (parts.length === 2 && parts[1]) {
      return `${wholeNumber}.${parts[1].slice(0, 2)}` // Limit to 2 decimal places
    }
    return wholeNumber + (value.endsWith('.') ? '.' : '')
  }

  // Format whole numbers with commas
  return cleanValue ? parseInt(cleanValue).toLocaleString('en-US') : ''
}

/**
 * Parse a formatted/typed input string back to a number. Strips currency
 * symbols, commas, and other non-numeric characters (keeping a leading minus
 * and a single decimal point), and returns `0` for empty, sign-only, or any
 * input that doesn't resolve to a finite number (e.g. `"-."`, a multi-dot
 * fragment) — so a blurred-but-incomplete field never yields `NaN`.
 *
 * @param {string} formattedValue - Formatted string (e.g. "1,234.56" or "$1,234.56")
 * @returns {number} Parsed number value (0 for empty/incomplete/non-finite input)
 *
 * @example
 * parseInputNumber("$1,234.56") // 1234.56
 * parseInputNumber("1,234")     // 1234
 * parseInputNumber("")          // 0
 * parseInputNumber("-.")        // 0  (incomplete — never NaN)
 */
export function parseInputNumber(formattedValue: string): number {
  // Return 0 for empty strings
  if (!formattedValue) return 0

  // Remove currency symbol, commas, and other non-numeric characters except decimal point and minus
  const cleanValue = formattedValue.replace(/[^0-9.-]/g, '')

  // Handle the obvious incomplete fragments up front
  if (!cleanValue || cleanValue === '-' || cleanValue === '.') return 0

  // Anything that still doesn't resolve to a finite number (e.g. "-.",
  // "1.2.3") normalizes to 0 rather than leaking NaN into caller state.
  const parsed = Number(cleanValue)
  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * Format a committed numeric value for display inside an input control, using
 * `Intl.NumberFormat`. Unlike `~/lib/utils/format` `formatNumberForDisplay`,
 * this accepts `number | string`, returns `''` (not `'—'`) for empty/`NaN`
 * input, and supports a fraction-digit *range* (`minimumFractionDigits` ≠
 * `maximumFractionDigits`) so trailing zeros can be dropped.
 *
 * @param {number | string} value - The value to format
 * @param {object} [options] - Formatting options
 * @param {boolean} [options.isCurrency] - Format as USD currency
 * @param {string} [options.locale='en-US'] - Locale for formatting
 * @param {number} [options.maximumFractionDigits=2] - Max fraction digits
 * @param {number} [options.minimumFractionDigits] - Min fraction digits (defaults to 2 for currency, else 0)
 * @returns {string} The formatted value, or '' for empty/non-numeric input
 *
 * @example
 * formatInputValue(1234.56, { isCurrency: true })                 // "$1,234.56"
 * formatInputValue(1234.56, { maximumFractionDigits: 1 })         // "1,234.6"
 * formatInputValue("", { isCurrency: true })                      // ""
 */
export function formatInputValue(
  value: number | string,
  options: {
    isCurrency?: boolean
    locale?: string
    maximumFractionDigits?: number
    minimumFractionDigits?: number
  } = {}
): string {
  // Handle empty or non-numeric input
  if (value === '' || value === null || value === undefined || Number.isNaN(Number(value))) {
    return ''
  }

  // Convert to number if string
  const numValue = typeof value === 'string' ? parseFloat(value) : value

  // Default options with nullish coalescing
  const {
    isCurrency = false,
    locale = 'en-US',
    maximumFractionDigits = 2,
    minimumFractionDigits = isCurrency ? 2 : 0
  } = options

  // Format using Intl.NumberFormat for proper locale-aware formatting
  return new Intl.NumberFormat(locale, {
    style: isCurrency ? 'currency' : 'decimal',
    currency: 'USD',
    minimumFractionDigits,
    maximumFractionDigits
  }).format(numValue)
}
